import { createInterfaceId } from '@gitlab/needle';
import type { WorkflowId } from '@gitlab-org/workflow-executor';
import type { ParsedCliInput } from '../../parse';
import { GitLabParsedOptions, isPreconfiguredCiRun } from './gitlab_parsed_options';

/**
 * The "run condition" of a single Duo CLI invocation, resolved once at startup.
 *
 * Everything the backend does about workflow lifecycle is a pure function of
 * this value, so lifecycle decisions read one explicit fact rather than
 * inspecting scattered command flags.
 *
 * The primary axis is `source` — WHO owns workflow creation — not the CLI
 * command. `duo run` straddles two sources (rails in CI, local otherwise); the
 * command name alone never answers "must I create a workflow?".
 */
export type RunCondition =
  | {
      /**
       * Headless CI. The GitLab monolith pre-created the workflow and injected
       * its id + a pre-built token. The CLI ATTACHES to `workflowId`; it never
       * creates a workflow, never runs a server default-flow lookup, and has no
       * direct_access capabilities (the pre-configured token carries none).
       *
       * Whether rails considers this a fresh run or a resume is rails' business
       * — from the CLI's side it is uniformly "attach to the id I was handed".
       */
      source: 'rails';
      presentation: 'headless';
      workflowId: WorkflowId;
    }
  | {
      /**
       * Interactive TUI and headless `run`. The CLI OWNS the workflow: it
       * creates a fresh one, or attaches to `resumeWorkflowId` when resuming its
       * own prior local session (in which case resume-probe UX applies). Both
       * presentations share every bit of this creation plumbing and differ only
       * in how their controller consumes the event stream.
       */
      source: 'local';
      presentation: 'interactive' | 'headless';
      resumeWorkflowId?: WorkflowId;
    };

export const RunCondition = createInterfaceId<RunCondition>('RunCondition');

export function resolveRunCondition(
  cliInput: ParsedCliInput,
  backendOpts: GitLabParsedOptions,
  injectedWorkflowId?: WorkflowId,
): RunCondition {
  const commandName = cliInput.command.name;
  const gitlabCommand = commandName === 'run' || commandName === 'tui' ? commandName : undefined;

  // `isPreconfiguredCiRun` is only ever true for `run` + CI token flags.
  if (isPreconfiguredCiRun(gitlabCommand, backendOpts)) {
    if (!injectedWorkflowId) {
      // A rails-owned run without an injected id is a contract violation: rails
      // always pre-creates the workflow and passes DUO_WORKFLOW_WORKFLOW_ID.
      throw new Error(
        'Headless CI run is missing the pre-created workflow id (DUO_WORKFLOW_WORKFLOW_ID). This is a bug.',
      );
    }
    return { source: 'rails', presentation: 'headless', workflowId: injectedWorkflowId };
  }

  // Local resume ids are threaded per BackendFactory.create(), so the startup
  // condition never carries one.
  return {
    source: 'local',
    presentation: commandName === 'tui' ? 'interactive' : 'headless',
  };
}

/**
 * Whether agent actions are auto-approved for this invocation. Resolved from the
 * startup {@link RunCondition} + the CLI opt-out flag, belonging to neither the
 * source axis (WorkflowStrategy) nor the presentation axis (PresentationPolicy):
 * it feeds both workflow-creation options and the privileges' pre-approval set,
 * so both read this single fact.
 *
 * True when there is no interactive user to prompt (headless `run` / rails CI)
 * or the user explicitly opted out of approvals
 * (`--dangerously-skip-permissions`).
 */
export function resolveAutoApprove(
  condition: RunCondition,
  dangerouslySkipPermissions: boolean,
): boolean {
  return condition.presentation === 'headless' || dangerouslySkipPermissions;
}
